'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface DIYTask {
    id: string;
    title: string;
    description: string;
    brand: 'BMW' | 'Lexus' | 'Audi' | 'Mercedes' | 'Toyota' | 'Honda' | 'Acura' | 'All';
    youtubeQuery: string;
    ebayQuery: string;
    category: 'Maintenance' | 'Interior' | 'Exterior' | 'Electronics';
}

const DIY_TASKS: DIYTask[] = [
    {
        id: 'wiper-blades',
        title: 'Change Wiper Blades',
        description: 'Keep your visibility clear by replacing worn wiper blades in minutes.',
        brand: 'All',
        youtubeQuery: 'how to change wiper blades',
        ebayQuery: 'wiper blades set',
        category: 'Maintenance'
    },
    {
        id: 'air-filter',
        title: 'Replace Engine Air Filter',
        description: 'Improve engine performance and fuel efficiency by replacing a dirty air filter.',
        brand: 'All',
        youtubeQuery: 'how to replace engine air filter',
        ebayQuery: 'engine air filter',
        category: 'Maintenance'
    },
    {
        id: 'cabin-filter',
        title: 'Replace Cabin Air Filter',
        description: 'Ensure clean air inside your vehicle by swapping out the cabin pollen filter.',
        brand: 'All',
        youtubeQuery: 'how to replace cabin air filter',
        ebayQuery: 'cabin air filter',
        category: 'Interior'
    },
    {
        id: 'remote-battery',
        title: 'Change Key Fob Battery',
        description: 'Low key battery? Easy fix to keep your remote entry working reliably.',
        brand: 'All',
        youtubeQuery: 'how to change car key fob battery',
        ebayQuery: 'CR2032 battery',
        category: 'Electronics'
    },
    {
        id: 'wiper-fluid',
        title: 'Add Wiper Fluid',
        description: 'A simple essential task to ensure you never run out of washer fluid.',
        brand: 'All',
        youtubeQuery: 'how to refill windshield washer fluid',
        ebayQuery: 'windshield washer fluid concentrate',
        category: 'Maintenance'
    },
    {
        id: 'battery-12v',
        title: 'Change 12V Battery',
        description: 'Don\'t get stranded. Replacing a weak 12V battery is a straightforward way to ensure your car starts every time.',
        brand: 'All',
        youtubeQuery: 'how to replace 12v car battery',
        ebayQuery: '12v car battery',
        category: 'Maintenance'
    },
    {
        id: 'rear-door-hydraulic-lift',
        title: 'Replace Rear Door Hydraulic Lift Supports',
        description: 'Restore smooth opening and holding power to your rear tailgate or trunk by replacing worn hydraulic gas struts.',
        brand: 'All',
        youtubeQuery: 'how to replace tailgate lift support struts',
        ebayQuery: 'tailgate lift support struts',
        category: 'Exterior'
    },
    {
        id: 'audi-mmi',
        title: 'Reset MMI System',
        description: 'Resolve frozen infotainment screens quickly by hard resetting your MMI system.',
        brand: 'Audi',
        youtubeQuery: 'how to reset Audi MMI screen',
        ebayQuery: 'Audi MMI removal tool',
        category: 'Electronics'
    },
    {
        id: 'merc-assyst',
        title: 'Reset Service Indicator',
        description: 'Clear the routine ASSYST PLUS service message from your dashboard after doing your own oil change.',
        brand: 'Mercedes',
        youtubeQuery: 'how to reset Mercedes service light',
        ebayQuery: 'Mercedes OBD2 scanner',
        category: 'Electronics'
    },
    {
        id: 'bmw-battery',
        title: 'Register 12V Battery',
        description: 'BMWs require new batteries to be registered so the alternator charges properly. Save hundreds doing it yourself.',
        brand: 'BMW',
        youtubeQuery: 'how to register bmw battery with scanner',
        ebayQuery: 'BMW OBD2 scanner battery registration',
        category: 'Electronics'
    },
    {
        id: 'bmw-idrive-reset',
        title: 'Reset iDrive Service Interval',
        description: 'Clear brake fluid, oil change, or vehicle check warnings from your iDrive screen using the odometer button.',
        brand: 'BMW',
        youtubeQuery: 'how to reset service light bmw',
        ebayQuery: 'bmw oil filter',
        category: 'Electronics'
    },
    {
        id: 'bmw-coolant',
        title: 'Top Up Engine Coolant',
        description: 'Safely check and top up the famously thirsty BMW cooling system using European spec antifreeze.',
        brand: 'BMW',
        youtubeQuery: 'how to add coolant bmw',
        ebayQuery: 'bmw blue coolant antifreeze',
        category: 'Maintenance'
    },
    {
        id: 'lexus-maint-req',
        title: 'Reset Maintenance Required Light',
        description: 'A simple combination of holding the trip meter button while turning on the ignition to save a trip to the dealer.',
        brand: 'Lexus',
        youtubeQuery: 'how to reset lexus maintenance required light',
        ebayQuery: 'lexus synthetic oil',
        category: 'Electronics'
    },
    {
        id: 'lexus-maf',
        title: 'Clean MAF Sensor',
        description: 'Fix jerky shifting and bad MPG by spraying down the Mass Air Flow sensor. A 10 minute job.',
        brand: 'Lexus',
        youtubeQuery: 'how to clean maf sensor lexus',
        ebayQuery: 'mass air flow sensor cleaner',
        category: 'Maintenance'
    },
    {
        id: 'lexus-pcv',
        title: 'Replace PCV Valve',
        description: 'Prevent massive oil leaks and blowing seals by unscrewing and replacing this cheap $15 valve.',
        brand: 'Lexus',
        youtubeQuery: 'how to replace pcv valve lexus',
        ebayQuery: 'lexus pcv valve oem',
        category: 'Maintenance'
    },
    {
        id: 'audi-ignition-coils',
        title: 'Replace Ignition Coils',
        description: 'A very common misfire fix on Audi A4/Q5 models. The red ignition coils literally just pop out by hand.',
        brand: 'Audi',
        youtubeQuery: 'how to replace ignition coils audi 2.0t',
        ebayQuery: 'audi r8 red ignition coils',
        category: 'Maintenance'
    },
    {
        id: 'audi-oil-topup',
        title: 'Top Up Engine Oil',
        description: 'Audi engines famously burn oil between changes. Learn how to check the MMI level and add the correct VW-502 spec oil.',
        brand: 'Audi',
        youtubeQuery: 'how to add oil audi mmi',
        ebayQuery: 'audi vw 502 00 synthetic oil',
        category: 'Maintenance'
    },
    {
        id: 'merc-aux-battery',
        title: 'Replace Auxiliary Battery',
        description: 'Fix the "Auxiliary Battery Malfunction" error that breaks Start/Stop functions. Usually located under the passenger footwell or trunk.',
        brand: 'Mercedes',
        youtubeQuery: 'mercedes auxiliary battery malfunction replacement',
        ebayQuery: 'mercedes auxiliary battery oem',
        category: 'Electronics'
    },
    {
        id: 'merc-sunroof-reset',
        title: 'Sunroof Reset Calibration',
        description: 'If your Mercedes sunroof gets stuck or one-touch close stops working, you can easily recalibrate it holding the switch.',
        brand: 'Mercedes',
        youtubeQuery: 'how to reset mercedes sunroof',
        ebayQuery: 'mercedes sunroof grease',
        category: 'Interior'
    },
    {
        id: 'acura-key-fob',
        title: 'Key Fob Battery Replacement',
        description: 'Replace the CR2032 battery in your Acura MDX, RDX, TLX, or Integra key remote in under 2 minutes.',
        brand: 'Acura',
        youtubeQuery: 'how to replace acura key fob battery',
        ebayQuery: 'CR2032 battery 5 pack',
        category: 'Electronics'
    },
    {
        id: 'acura-cabin-filter',
        title: 'Cabin Air Filter Replacement',
        description: 'Drop down the glovebox to swap in a clean cabin pollen filter for odor-free AC airflow.',
        brand: 'Acura',
        youtubeQuery: 'how to replace acura cabin air filter',
        ebayQuery: 'Acura cabin air filter',
        category: 'Interior'
    },
    {
        id: 'acura-oil-life-reset',
        title: 'Maintenance Minder Oil Life Reset',
        description: 'Reset the dash Oil Life reading back to 100% after changing your oil using the multi-information display.',
        brand: 'Acura',
        youtubeQuery: 'how to reset acura maintenance minder oil life',
        ebayQuery: 'Acura oil filter OEM',
        category: 'Maintenance'
    }
];

const DIYInteractive = () => {
    const [isHydrated, setIsHydrated] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<'BMW' | 'Lexus' | 'Audi' | 'Mercedes' | 'Toyota' | 'Honda' | 'Acura' | 'All'>('All');

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    if (!isHydrated) return null;

    const filteredTasks = DIY_TASKS.filter(task =>
        selectedBrand === 'All' || task.brand === 'All' || task.brand === selectedBrand
    );

    const handleWatchYouTube = (task: DIYTask) => {
        const brandPrefix = selectedBrand === 'All' && task.brand !== 'All' ? `${task.brand} ` : selectedBrand === 'All' ? '' : `${selectedBrand} `;
        const query = encodeURIComponent(`${brandPrefix}${task.youtubeQuery}`);
        window.open(`https://www.youtube.com/results?search_query=${query}&sp=CAM%253D`, '_blank');
    };

    const handleFindProduct = (task: DIYTask) => {
        const brandPrefix = selectedBrand === 'All' ? '' : `${selectedBrand} `;
        const query = encodeURIComponent(`${brandPrefix}${task.ebayQuery}`);
        window.location.href = `/oem-parts-finder?q=${query}`;
    };

    return (
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="text-center mb-12">
                <h1 className="font-heading text-4xl font-bold text-text-primary mb-4">
                    DIY Maintenance Guides
                </h1>
                <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
                    Save time and money by performing these simple maintenance tasks yourself.
                    Follow expert videos and find the exact parts you need.
                </p>
            </div>

            {/* Brand Filter */}
            <div className="flex justify-center space-x-4 mb-12 flex-wrap gap-y-4">
                {['All', 'BMW', 'Lexus', 'Audi', 'Mercedes', 'Toyota', 'Honda', 'Acura'].map((brand) => (
                    <button
                        key={brand}
                        onClick={() => setSelectedBrand(brand as any)}
                        className={`px-6 py-2 rounded-full font-medium transition-smooth ${selectedBrand === brand
                            ? 'bg-primary text-white shadow-elevation-md'
                            : 'bg-muted text-text-secondary hover:bg-muted/80'
                            }`}
                    >
                        {brand}
                    </button>
                ))}
            </div>

            {/* Tasks Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredTasks.map((task) => (
                    <div
                        key={task.id}
                        className="group bg-card rounded-xl overflow-hidden shadow-elevation-sm hover:shadow-elevation-lg transition-smooth border border-border"
                    >
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                                    {task.category}
                                </span>
                                <Icon name="WrenchScrewdriverIcon" size={20} className="text-muted-foreground group-hover:text-primary transition-smooth" />
                            </div>

                            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">
                                {task.title}
                            </h3>
                            <p className="font-body text-sm text-text-secondary mb-6 leading-relaxed">
                                {task.description}
                            </p>

                            <div className="flex flex-col space-y-3">
                                <button
                                    onClick={() => handleWatchYouTube(task)}
                                    className="flex items-center justify-center space-x-2 w-full rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 transition-smooth"
                                >
                                    <Icon name="PlayCircleIcon" size={18} />
                                    <span>Watch YouTube</span>
                                </button>

                                <button
                                    onClick={() => handleFindProduct(task)}
                                    className="flex items-center justify-center space-x-2 w-full rounded-lg border border-primary text-primary px-4 py-2.5 text-sm font-medium hover:bg-primary/5 transition-smooth"
                                >
                                    <Icon name="MagnifyingGlassIcon" size={18} />
                                    <span>Find Product</span>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-16 p-6 rounded-xl bg-orange-50 border border-orange-200">
                <div className="flex space-x-3">
                    <Icon name="InformationCircleIcon" size={24} className="text-orange-500 flex-shrink-0" />
                    <p className="text-sm text-orange-800 leading-relaxed">
                        <strong>Disclaimer:</strong> DIY guides are for informational purposes. Always consult your vehicle's manual.
                        If you are unsure about a repair, we recommend visiting a <Link href="/home-page" className="underline font-semibold text-primary">verified specialist</Link>.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DIYInteractive;
